import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

/** UI / table — derived from API `isActive`. */
export type TrainerCommissionStatus = 'ACTIVE' | 'INACTIVE'

export enum TrainerCommissionConductType {
  TRAINING = 'TRAINING',
  CLASS = 'CLASS',
}

export enum TrainerCommissionSessionStatusApi {
  SHOW = 'SHOW',
  NO_SHOW = 'NO_SHOW',
}

export enum TrainerCommissionContractTypeApi {
  FREELANCE = 'FREELANCE',
  PERMANENT = 'PERMANENT',
}

export enum TrainerConductRateType {
  PERCENT = 'PERCENT',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

@Serializable()
export class TrainerCommissionRoleEntity extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class TrainerCommissionRoleContractEntity extends Fromable {
  @JsonProperty({
    path: 'role',
    type: TrainerCommissionRoleEntity,
  })
  role!: TrainerCommissionRoleEntity

  @JsonProperty('contractTypes')
  contractTypes!: TrainerCommissionContractTypeApi[]
}

@Serializable()
export class TrainerCommissionClassCategoryEntity extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class TrainerCommissionTrainingProgramEntity extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty('nameTh')
  nameTh!: string
}

@Serializable()
export class TrainerConductConditionEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('orderIndex')
  orderIndex!: number

  @JsonProperty('sessionCountFrom')
  sessionCountFrom!: number

  /** API may send number or null for open-ended range */
  @JsonProperty('sessionCountTo')
  sessionCountTo!: number | null

  @JsonProperty('rateType')
  rateType!: TrainerConductRateType

  @JsonProperty('rate')
  rate!: number
}

@Serializable()
export class TrainerCommissionRuleEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('conductType')
  conductType!: TrainerCommissionConductType

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty('trainerLevels')
  trainerLevels!: string[]

  @JsonProperty('sessionStatus')
  sessionStatus!: TrainerCommissionSessionStatusApi[]

  @JsonProperty('contractTypes')
  contractTypes!: TrainerCommissionContractTypeApi[]

  @JsonProperty({
    path: 'roleContracts',
    type: TrainerCommissionRoleContractEntity,
    elementType: TrainerCommissionRoleContractEntity,
  })
  roleContracts!: TrainerCommissionRoleContractEntity[]

  @JsonProperty({
    path: 'classCategories',
    type: TrainerCommissionClassCategoryEntity,
    elementType: TrainerCommissionClassCategoryEntity,
  })
  classCategories!: TrainerCommissionClassCategoryEntity[]

  @JsonProperty({
    path: 'trainingPrograms',
    type: TrainerCommissionTrainingProgramEntity,
    elementType: TrainerCommissionTrainingProgramEntity,
  })
  trainingPrograms!: TrainerCommissionTrainingProgramEntity[]

  @JsonProperty({
    path: 'conditions',
    type: TrainerConductConditionEntity,
    elementType: TrainerConductConditionEntity,
  })
  conditions!: TrainerConductConditionEntity[]
}

@Serializable()
export class CreateTrainerCommissionRoleContractDto extends Fromable {
  @JsonProperty('roleIds')
  roleIds!: number[]

  @JsonProperty('contractTypes')
  contractTypes!: TrainerCommissionContractTypeApi[]
}

@Serializable()
export class CreateTrainerCommissionConditionDto extends Fromable {
  @JsonProperty('sessionCountFrom')
  sessionCountFrom!: number

  @JsonProperty('sessionCountTo')
  sessionCountTo!: number | null

  @JsonProperty('rateType')
  rateType!: TrainerConductRateType

  @JsonProperty('rate')
  rate!: number
}

@Serializable()
export class CreateTrainerCommissionRuleDto extends Fromable {
  @JsonProperty('name')
  name!: string

  @JsonProperty('conductType')
  conductType!: TrainerCommissionConductType

  @JsonProperty('trainerLevels')
  trainerLevels!: string[]

  @JsonProperty('sessionStatus')
  sessionStatus?: TrainerCommissionSessionStatusApi[]

  @JsonProperty('trainingProgramIds')
  trainingProgramIds?: number[]

  @JsonProperty('classCategoryIds')
  classCategoryIds?: number[]

  @JsonProperty({
    path: 'roleContracts',
    type: CreateTrainerCommissionRoleContractDto,
    elementType: CreateTrainerCommissionRoleContractDto,
  })
  roleContracts!: CreateTrainerCommissionRoleContractDto[]

  @JsonProperty({
    path: 'conditions',
    type: CreateTrainerCommissionConditionDto,
    elementType: CreateTrainerCommissionConditionDto,
  })
  conditions!: CreateTrainerCommissionConditionDto[]
}

@Serializable()
export class UpdateTrainerCommissionRuleDto extends Fromable {
  @JsonProperty('name')
  name!: string

  @JsonProperty('conductType')
  conductType!: TrainerCommissionConductType

  @JsonProperty('trainerLevels')
  trainerLevels!: string[]

  @JsonProperty('sessionStatus')
  sessionStatus?: TrainerCommissionSessionStatusApi[]

  @JsonProperty('trainingProgramIds')
  trainingProgramIds?: number[]

  @JsonProperty('classCategoryIds')
  classCategoryIds?: number[]

  @JsonProperty({
    path: 'roleContracts',
    type: CreateTrainerCommissionRoleContractDto,
    elementType: CreateTrainerCommissionRoleContractDto,
  })
  roleContracts!: CreateTrainerCommissionRoleContractDto[]

  @JsonProperty({
    path: 'conditions',
    type: CreateTrainerCommissionConditionDto,
    elementType: CreateTrainerCommissionConditionDto,
  })
  conditions!: CreateTrainerCommissionConditionDto[]
}

@Serializable()
export class UpdateTrainerCommissionRuleStatusDto extends Fromable {
  @JsonProperty('isActive')
  isActive!: boolean
}

/** List row / shared fields — mapped from {@link TrainerCommissionRuleEntity}. */
export interface TrainerCommissionCondition {
  id: string
  status: TrainerCommissionStatus
  commissionRuleName: string
  roles: string[]
  trainerLevels: string[]
  sessionStatuses: string[]
  trainingProgramIds?: number[]
  /** Display labels for list (API `trainingPrograms[].nameEn`) */
  trainingProgramNames?: string[]
  contractType: string[]
  conditionKind?: 'training' | 'class'
  classCategories?: string[]
}

export interface TrainerCommissionBand {
  conditionLabel: string
  conductRatePercent: number | null
  conductFeeThb: number | null
}

export interface TrainerCommissionConditionDetail extends TrainerCommissionCondition {
  commissionBands: TrainerCommissionBand[]
  /** API conduct tiers (sorted), same source as {@link commissionBands}. */
  conditions: TrainerConductConditionEntity[]
  isActive: boolean
  /** For edit form — API `classCategories[].id` */
  classCategoryIds?: number[]
  /** Grouped from API `roleContracts` for form prefill */
  roleContractRowsForEdit?: { roles: number[]; contractTypes: string[] }[]
}
